import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface DialogProps {
  open: boolean;
  children: ReactNode;
}

export function Dialog({ open, children }: Readonly<DialogProps>): JSX.Element | null {
  if (!open) {
    return null;
  }

  return <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">{children}</div>;
}

export const DialogOverlay = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('fixed inset-0 bg-foreground/50', className)} {...props} />
  ),
);

DialogOverlay.displayName = 'DialogOverlay';

export const DialogContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative z-[1] max-h-[90vh] w-full max-w-[35rem] overflow-y-auto rounded-xl border border-border bg-card p-4 shadow-[0_14px_30px_rgba(15,23,42,0.2)]',
        className,
      )}
      {...props}
    />
  ),
);

DialogContent.displayName = 'DialogContent';

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props} />
  ),
);

DialogHeader.displayName = 'DialogHeader';

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-wrap justify-end gap-2', className)} {...props} />
  ),
);

DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} className={cn('m-0 text-base font-bold leading-5 text-foreground', className)} {...props} />
  ),
);

DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('m-0 text-[13px] leading-[1.4] text-muted-foreground', className)}
      {...props}
    />
  ),
);

DialogDescription.displayName = 'DialogDescription';
